import queryString from 'query-string';
import { useHistory, useLocation } from 'react-router';

import { routes } from '@/config';
import { extractFiltersFromQueryUnflattened } from '@/helpers/filters-summary';
import { fromOldFormatQuery, getQueryParams } from '@/helpers/query-string';
import { createQueryFromUrl } from '@/helpers/search-helpers';
import { useExtractX1Filters } from '@/hooks/use-extract-x1-filters';

type UseRedirectLegacyURL = {
  isLegacyURL?: boolean;
};

// We need to redirect from the legacy to the new search page for the following cases:
// - Search Alert Emails (link could be changed to include the new filters or the search guid https://jira.xing.hh/browse/JSR-1187)
// - External sites (eg https://jobs-ch.xing.com/, https://jobs-at.xing.com/, and there may be others)
// - JDP Navigation back button (to be migrated in https://jira.xing.hh/browse/JDI-2169 or removed)
export function useRedirectLegacyURL(): UseRedirectLegacyURL {
  const history = useHistory();
  const { search = '' } = useLocation();

  // Check for legacy filters (eg: filter.type[]=FULL_TIME)
  const query = createQueryFromUrl(search);
  const extractedFilters = extractFiltersFromQueryUnflattened({}, { query });
  const { x1Filters, isLoading } = useExtractX1Filters(extractedFilters);
  const hasLegacyFilters = Object.keys(x1Filters).length > 0;

  // Check for legacy pagination (eg: limit=20&offset=0)
  const { offset, limit } = fromOldFormatQuery(getQueryParams(search));
  const hasLegacyPagination = Number(offset) >= 0 && Number(limit) > 0;

  if (!isLoading && (hasLegacyFilters || hasLegacyPagination)) {
    const originalParams = queryString.parse(search, { arrayFormat: 'bracket' });
    const removeLegacyFilters = Object.entries(originalParams).filter(
      ([key]) => !key.startsWith('filter') && key !== 'offset' && key !== 'limit'
    );
    const filteredOriginalParams = Object.fromEntries(removeLegacyFilters);

    const urlParamsX1 = {
      ...filteredOriginalParams,
      ...(hasLegacyFilters && x1Filters),
      ...(hasLegacyPagination && { page: Number(offset) / Number(limit) + 1 }),
    };

    history.replace(`${routes.search}?${queryString.stringify(urlParamsX1)}`);
  }

  return { isLegacyURL: isLoading || hasLegacyFilters || hasLegacyPagination };
}
